/**
 * Shell UI for the interactive canvas: dialogs, menus, grid toggle, quick-add,
 * toolbar popover coordination (state + setter for Radix / `useCanvasOnNodesChange`).
 * Thumbnail capture iframe uses `variant="capture"` (noop toolbar + frozen defaults).
 */

import {
  CANVAS_CONTROLS_STORAGE_KEYS,
  parseCanvasCursorTool,
  parseCanvasGridMode,
  type CanvasCursorTool,
  type CanvasGridMode,
} from "@/pages/canvas/canvas-controls.types";
import type { ConnectorPreset } from "@/pages/canvas/hooks/useConnectorPlacement";
import { useLocalStorageState } from "ahooks";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type RefObject,
  type SetStateAction,
} from "react";

export interface QuickAddCursorState {
  screenX: number;
  screenY: number;
  clientX: number;
  clientY: number;
  flowPosition: { x: number; y: number };
}

export interface NodeContextMenuState {
  nodeId: string;
  nodeType: string | undefined;
  screenX: number;
  screenY: number;
  clientX: number;
  clientY: number;
  flowPosition: { x: number; y: number };
}

export interface CanvasUiContextType {
  /** Derived: true when any grid variant is visible. */
  showGrid: boolean;
  gridMode: CanvasGridMode;
  setGridMode: Dispatch<SetStateAction<CanvasGridMode>>;
  cursorTool: CanvasCursorTool;
  setCursorTool: Dispatch<SetStateAction<CanvasCursorTool>>;
  isWidgetsMenuOpen: boolean;
  setIsWidgetsMenuOpen: Dispatch<SetStateAction<boolean>>;
  isLinkMenuOpen: boolean;
  setIsLinkMenuOpen: Dispatch<SetStateAction<boolean>>;
  showQuickAdd: boolean;
  setShowQuickAdd: Dispatch<SetStateAction<boolean>>;
  quickAddCursor: QuickAddCursorState | null;
  setQuickAddCursor: Dispatch<SetStateAction<QuickAddCursorState | null>>;
  nodeContextMenu: NodeContextMenuState | null;
  setNodeContextMenu: Dispatch<SetStateAction<NodeContextMenuState | null>>;
  isShapesMenuOpen: boolean;
  setIsShapesMenuOpen: Dispatch<SetStateAction<boolean>>;
  isNoteMenuOpen: boolean;
  setIsNoteMenuOpen: Dispatch<SetStateAction<boolean>>;
  activeConnectorPreset: ConnectorPreset | null;
  setActiveConnectorPreset: Dispatch<SetStateAction<ConnectorPreset | null>>;
  isHoveringControls: boolean;
  setIsHoveringControls: Dispatch<SetStateAction<boolean>>;
  /**
   * True while any canvas toolbar popover (node toolbars, edge toolbar, etc.) is open.
   * Popovers call `setToolbarPopoverOpen` on open/close so `useCanvasOnNodesChange` can
   * filter stray `select: false` from Radix portal dismissal.
   */
  isToolbarPopoverOpen: boolean;
  setToolbarPopoverOpen: Dispatch<SetStateAction<boolean>>;
  /** Edge currently being label-edited inline (double-click / toolbar). null = none. */
  editingEdgeId: string | null;
  setEditingEdgeId: Dispatch<SetStateAction<string | null>>;
  /** Outer React Flow wrapper div; assigned by `CanvasPageInner` ref callback. */
  reactFlowWrapperRef: RefObject<HTMLDivElement | null>;
  /** Same element as `reactFlowWrapperRef.current`; state so layout hooks can subscribe. */
  reactFlowWrapperEl: HTMLDivElement | null;
  setReactFlowWrapperEl: Dispatch<SetStateAction<HTMLDivElement | null>>;
  isResizingRef: RefObject<boolean>;
}

const CanvasUiContext = createContext<CanvasUiContextType | null>(null);

const noopDispatchBool: Dispatch<SetStateAction<boolean>> = () => {};
const noopDispatchQuickAdd: Dispatch<
  SetStateAction<QuickAddCursorState | null>
> = () => {};
const noopDispatchNodeCtx: Dispatch<
  SetStateAction<NodeContextMenuState | null>
> = () => {};
const noopDispatchConnector: Dispatch<
  SetStateAction<ConnectorPreset | null>
> = () => {};
const noopDispatchGridMode: Dispatch<SetStateAction<CanvasGridMode>> = () => {};
const noopDispatchCursorTool: Dispatch<
  SetStateAction<CanvasCursorTool>
> = () => {};
const noopDispatchHover: Dispatch<SetStateAction<boolean>> = () => {};
const noopSetReactFlowWrapperEl: Dispatch<
  SetStateAction<HTMLDivElement | null>
> = () => {};

const CAPTURE_REACT_FLOW_WRAPPER_REF: RefObject<HTMLDivElement | null> = {
  current: null,
};

/** Static value for thumbnail capture iframe (shared node types call `useCanvasUi`). */
const CANVAS_UI_CAPTURE_VALUE: CanvasUiContextType = {
  showGrid: true,
  gridMode: "lines",
  setGridMode: noopDispatchGridMode,
  cursorTool: "arrow",
  setCursorTool: noopDispatchCursorTool,
  isWidgetsMenuOpen: false,
  setIsWidgetsMenuOpen: noopDispatchBool,
  isLinkMenuOpen: false,
  setIsLinkMenuOpen: noopDispatchBool,
  showQuickAdd: false,
  setShowQuickAdd: noopDispatchBool,
  quickAddCursor: null,
  setQuickAddCursor: noopDispatchQuickAdd,
  nodeContextMenu: null,
  setNodeContextMenu: noopDispatchNodeCtx,
  isShapesMenuOpen: false,
  setIsShapesMenuOpen: noopDispatchBool,
  isNoteMenuOpen: false,
  setIsNoteMenuOpen: noopDispatchBool,
  activeConnectorPreset: null,
  setActiveConnectorPreset: noopDispatchConnector,
  isHoveringControls: false,
  setIsHoveringControls: noopDispatchHover,
  isToolbarPopoverOpen: false,
  setToolbarPopoverOpen: noopDispatchBool,
  editingEdgeId: null,
  setEditingEdgeId: () => {},
  reactFlowWrapperRef: CAPTURE_REACT_FLOW_WRAPPER_REF,
  reactFlowWrapperEl: null,
  setReactFlowWrapperEl: noopSetReactFlowWrapperEl,
  isResizingRef: { current: false },
};

export function useCanvasUi(): CanvasUiContextType {
  const ctx = useContext(CanvasUiContext);
  if (!ctx) {
    throw new Error("useCanvasUi must be used within CanvasUiProvider");
  }
  return ctx;
}

export function CanvasUiProvider({
  children,
  variant = "interactive",
}: {
  children: ReactNode;
  variant?: "interactive" | "capture" | "view";
}) {
  if (variant === "capture") {
    return (
      <CanvasUiContext.Provider value={CANVAS_UI_CAPTURE_VALUE}>
        {children}
      </CanvasUiContext.Provider>
    );
  }

  return <CanvasUiInteractiveProvider>{children}</CanvasUiInteractiveProvider>;
}

function CanvasUiInteractiveProvider({ children }: { children: ReactNode }) {
  const [gridModeRaw, setGridModeRaw] = useLocalStorageState<CanvasGridMode>(
    CANVAS_CONTROLS_STORAGE_KEYS.gridMode,
    { defaultValue: "lines" },
  );
  const gridMode = parseCanvasGridMode(gridModeRaw);
  const [cursorToolRaw, setCursorToolRaw] =
    useLocalStorageState<CanvasCursorTool>(
      CANVAS_CONTROLS_STORAGE_KEYS.cursorTool,
      { defaultValue: "arrow" },
    );
  const cursorTool = parseCanvasCursorTool(cursorToolRaw);
  const showGrid = gridMode !== "none";

  const setGridMode = useCallback<Dispatch<SetStateAction<CanvasGridMode>>>(
    (value) => {
      setGridModeRaw((prev) => {
        if (typeof value === "function") {
          return value(parseCanvasGridMode(prev));
        }
        return value;
      });
    },
    [setGridModeRaw],
  );

  const setCursorTool = useCallback<Dispatch<SetStateAction<CanvasCursorTool>>>(
    (value) => {
      setCursorToolRaw((prev) => {
        if (typeof value === "function") {
          return value(parseCanvasCursorTool(prev));
        }
        return value;
      });
    },
    [setCursorToolRaw],
  );

  const [isWidgetsMenuOpen, setIsWidgetsMenuOpen] = useState(false);
  const [isLinkMenuOpen, setIsLinkMenuOpen] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddCursor, setQuickAddCursor] =
    useState<QuickAddCursorState | null>(null);
  const [nodeContextMenu, setNodeContextMenu] =
    useState<NodeContextMenuState | null>(null);
  const [isShapesMenuOpen, setIsShapesMenuOpen] = useState(false);
  const [isNoteMenuOpen, setIsNoteMenuOpen] = useState(false);
  const [activeConnectorPreset, setActiveConnectorPreset] =
    useState<ConnectorPreset | null>(null);
  const [isHoveringControls, setIsHoveringControls] = useState(false);
  const [isToolbarPopoverOpen, setToolbarPopoverOpen] = useState(false);
  const [editingEdgeId, setEditingEdgeId] = useState<string | null>(null);
  const reactFlowWrapperRef = useRef<HTMLDivElement>(null);
  const [reactFlowWrapperEl, setReactFlowWrapperEl] =
    useState<HTMLDivElement | null>(null);

  const isResizingRef = useRef(false);

  const value = useMemo(
    (): CanvasUiContextType => ({
      showGrid,
      gridMode,
      setGridMode,
      cursorTool,
      setCursorTool,
      isWidgetsMenuOpen,
      setIsWidgetsMenuOpen,
      isLinkMenuOpen,
      setIsLinkMenuOpen,
      showQuickAdd,
      setShowQuickAdd,
      quickAddCursor,
      setQuickAddCursor,
      nodeContextMenu,
      setNodeContextMenu,
      isShapesMenuOpen,
      setIsShapesMenuOpen,
      isNoteMenuOpen,
      setIsNoteMenuOpen,
      activeConnectorPreset,
      setActiveConnectorPreset,
      isHoveringControls,
      setIsHoveringControls,
      isToolbarPopoverOpen,
      setToolbarPopoverOpen,
      editingEdgeId,
      setEditingEdgeId,
      reactFlowWrapperRef,
      reactFlowWrapperEl,
      setReactFlowWrapperEl,
      isResizingRef,
    }),
    [
      showGrid,
      gridMode,
      setGridMode,
      cursorTool,
      setCursorTool,
      isWidgetsMenuOpen,
      isLinkMenuOpen,
      showQuickAdd,
      quickAddCursor,
      nodeContextMenu,
      isShapesMenuOpen,
      isNoteMenuOpen,
      activeConnectorPreset,
      isHoveringControls,
      isToolbarPopoverOpen,
      editingEdgeId,
      reactFlowWrapperEl,
      isResizingRef,
    ],
  );

  return (
    <CanvasUiContext.Provider value={value}>
      {children}
    </CanvasUiContext.Provider>
  );
}
